import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppThunk } from './store';
import { apiString as API_URL } from '../utils/constants';
import axios from 'axios';

export interface ChpAiCitationDto {
    id?: string;
    title?: string;
    source?: string;
    url?: string;
    excerpt?: string;
}

export interface MemberAiSummaryDto {
    id: string;
    memberId: string;
    markdown: string;
    sections: Record<string, string>;
    citations: ChpAiCitationDto[];
    generatedAt: string;
    generatedByModel: string;
    promptTokens: number;
    completionTokens: number;
    inputHash: string;
    isStale: boolean;
}

export interface MemberAiSummaryProgressDto {
    stage: string;
    isComplete: boolean;
    hasError: boolean;
}

export interface MemberAiSummaryState {
    summary: MemberAiSummaryDto | null;
    progress: MemberAiSummaryProgressDto | null;
    isLoading: boolean;
    isGenerating: boolean;
    error: string | null;
}

const initialState: MemberAiSummaryState = {
    summary: null,
    progress: null,
    isLoading: false,
    isGenerating: false,
    error: null,
};

const memberAiSummarySlice = createSlice({
    name: 'memberAiSummary',
    initialState,
    reducers: {
        setSummary: (state, action: PayloadAction<MemberAiSummaryDto | null>) => {
            state.summary = action.payload;
        },
        setProgress: (state, action: PayloadAction<MemberAiSummaryProgressDto | null>) => {
            state.progress = action.payload;
        },
        setIsLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
        },
        setIsGenerating: (state, action: PayloadAction<boolean>) => {
            state.isGenerating = action.payload;
        },
        setError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload;
        },
        resetState: (state) => {
            state.summary = null;
            state.progress = null;
            state.isLoading = false;
            state.isGenerating = false;
            state.error = null;
        },
    },
});

export const { setSummary, setProgress, setIsLoading, setIsGenerating, setError, resetState } = memberAiSummarySlice.actions;

// --- Thunks ---

export const fetchMemberSummary = (memberId: string): AppThunk => async (dispatch) => {
    dispatch(setIsLoading(true));
    dispatch(setError(null));
    dispatch(setSummary(null));
    dispatch(setProgress(null));
    try {
        const response = await axios.get(`${API_URL}/membersummary/cached/${memberId}`);
        if (response.status === 200 && response.data) {
            // Map dual casing
            const s: MemberAiSummaryDto = {
                id: response.data.Id ?? response.data.id,
                memberId: response.data.MemberId ?? response.data.memberId,
                markdown: response.data.Markdown ?? response.data.markdown ?? '',
                sections: response.data.Sections ?? response.data.sections ?? {},
                citations: (response.data.Citations ?? response.data.citations ?? []).map((c: any) => ({
                    id: c.Id ?? c.id,
                    title: c.Title ?? c.title,
                    source: c.Source ?? c.source,
                    url: c.Url ?? c.url,
                    excerpt: c.Excerpt ?? c.excerpt,
                })),
                generatedAt: response.data.GeneratedAt ?? response.data.generatedAt,
                generatedByModel: response.data.GeneratedByModel ?? response.data.generatedByModel,
                promptTokens: response.data.PromptTokens ?? response.data.promptTokens ?? 0,
                completionTokens: response.data.CompletionTokens ?? response.data.completionTokens ?? 0,
                inputHash: response.data.InputHash ?? response.data.inputHash ?? '',
                isStale: response.data.IsStale ?? response.data.isStale ?? false,
            };
            dispatch(setSummary(s));
        } else {
            dispatch(setSummary(null));
        }
    } catch (e: any) {
        dispatch(setError(e.response?.data || 'Failed to fetch summary.'));
        dispatch(setSummary(null));
    } finally {
        dispatch(setIsLoading(false));
    }
};

export const triggerSummaryGeneration = (memberId: string, force: boolean = false): AppThunk => async (dispatch) => {
    dispatch(setIsGenerating(true));
    dispatch(setError(null));
    try {
        await axios.post(`${API_URL}/membersummary/generate/${memberId}?force=${force}`);
        // Start polling progress
        dispatch(pollSummaryProgress(memberId));
    } catch (e: any) {
        dispatch(setError(e.response?.data || 'Failed to trigger summary generation.'));
        dispatch(setIsGenerating(false));
    }
};

let pollInterval: ReturnType<typeof setInterval> | null = null;
const POLL_INTERVAL_MS = 1500;
const POLL_MAX_ATTEMPTS = 120; // ~3 minutes before giving up to avoid infinite polling
let pollAttempts = 0;

export const pollSummaryProgress = (memberId: string): AppThunk => async (dispatch) => {
    if (pollInterval) clearInterval(pollInterval);
    pollAttempts = 0;

    const stopPolling = () => {
        if (pollInterval) {
            clearInterval(pollInterval);
            pollInterval = null;
        }
    };

    const checkProgress = async () => {
        pollAttempts++;
        // Safety bound: never poll forever. If the backend progress entry was lost
        // (e.g. app-domain recycle) or generation stalled, give up after a bounded time.
        if (pollAttempts > POLL_MAX_ATTEMPTS) {
            stopPolling();
            dispatch(setIsGenerating(false));
            dispatch(setError('Summary generation is taking longer than expected. Please try again.'));
            return;
        }
        try {
            const res = await axios.get(`${API_URL}/membersummary/progress/${memberId}`);
            if (res.data) {
                const p: MemberAiSummaryProgressDto = {
                    stage: res.data.Stage ?? res.data.stage ?? 'Synthesizing...',
                    isComplete: res.data.IsComplete ?? res.data.isComplete ?? false,
                    hasError: res.data.HasError ?? res.data.hasError ?? false,
                };
                dispatch(setProgress(p));

                if (p.isComplete || p.hasError) {
                    stopPolling();
                    dispatch(setIsGenerating(false));
                    // Fetch completed summary
                    dispatch(fetchMemberSummary(memberId));
                }
            }
        } catch {
            // keep polling; the attempt counter above bounds total tries.
        }
    };

    // run immediately
    await checkProgress();

    pollInterval = setInterval(checkProgress, POLL_INTERVAL_MS);
};

export const stopProgressPolling = (): AppThunk => () => {
    if (pollInterval) {
        clearInterval(pollInterval);
        pollInterval = null;
    }
    pollAttempts = 0;
};

export default memberAiSummarySlice.reducer;
